import { useScreenTheme } from '@/providers/ScreenThemeStateProvider/EventScreenThemeContext';
import useWindowSize from '@/hooks/useWindowSize';
import { useMemo } from 'react';
import QRCode from 'react-qr-code';
import { useSearchParams } from 'react-router-dom';
import { StageEngagementFragment } from '@/generated/graphql';
import {
  getFreeformStageQrFootprintPx,
  getStageJoinQrCodeProps,
  qrCodePropsFromFootprint,
  stageQrCaptionClassName,
} from './stageJoinQr';
import {
  SLIDE_QR_CAPTION_RESERVE_FONT_SIZE,
  SLIDE_QR_LABEL_FONT_SIZE,
} from '@/engagements/slides/slideFrame';

export const StageQR = ({
  hideCta = false,
  mutedAppearance = false,
  slideSize,
}: {
  hideCta?: boolean;
  mutedAppearance?: boolean;
  slideSize?: { width: number; height: number };
}) => {
  const [searchParams] = useSearchParams();
  const engagementIdOverride = searchParams.get('engagementId');
  const { width: windowWidth } = useWindowSize();
  const { state } = useScreenTheme();
  const isFreeform = slideSize != null;
  const layoutWidth = slideSize?.width ?? windowWidth;
  const layoutHeight = slideSize?.height;

  const isStageEngagement = (eng: any): eng is StageEngagementFragment => {
    return eng && 'qrCodeCta' in eng;
  };

  const activeEngagement = state.activeEngagement;
  const qrCodeCta = isStageEngagement(activeEngagement)
    ? activeEngagement.qrCodeCta
    : undefined;
  const hasCaption = Boolean(qrCodeCta && qrCodeCta.trim());
  const omitCaption = hideCta || !hasCaption;

  const qrCodeProps = isFreeform
    ? qrCodePropsFromFootprint(
        getFreeformStageQrFootprintPx(slideSize.width, slideSize.height, {
          mutedAppearance,
          hideCta: omitCaption,
        }),
      )
    : getStageJoinQrCodeProps(windowWidth);
  const wrapperBackgroundColor = mutedAppearance
    ? 'rgba(48,48,48,0.95)'
    : state.savedTheme?.qrWrapperBackgroundColor || 'rgba(255,255,255,1)';

  const captionColor = mutedAppearance
    ? 'rgba(210,210,210,1)'
    : state.savedTheme?.qrForegroundColor || 'rgba(0,0,0,1)';

  const qrValue = useMemo(() => {
    if (engagementIdOverride) {
      return `${window.location.origin}/e/${state.eventId}?engagementId=${engagementIdOverride}`;
    }
    return `${window.location.origin}/e/${state.eventId}`;
  }, [engagementIdOverride, state.eventId]);

  const {
    stageQrContainerStyles,
    stageQrCaptionSlotStyles,
    stageQrTextStyles,
    stageQrRemainingStyles,
    stageQrTileStyles,
    stageQrStyles,
  } = useMemo(
    () => ({
      stageQrContainerStyles: {
        backgroundColor: wrapperBackgroundColor,
        padding:
          layoutHeight != null
            ? mutedAppearance
              ? `${layoutHeight * 0.014}px ${layoutWidth * 0.012}px`
              : `${layoutHeight * 0.02}px ${layoutWidth * 0.015}px`
            : mutedAppearance
              ? '1.4vh 1.2vw'
              : '2vh 1.5vw',
        height: layoutHeight != null ? '100%' : undefined,
        boxSizing: 'border-box' as const,
      },
      stageQrCaptionSlotStyles: {
        height: SLIDE_QR_CAPTION_RESERVE_FONT_SIZE,
        width: '100%',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
      },
      stageQrTextStyles: {
        color: captionColor,
        fontSize: slideSize ? SLIDE_QR_LABEL_FONT_SIZE : '1.8vw',
        ...(slideSize ? { width: '100%' as const } : {}),
      },
      stageQrRemainingStyles: {
        flex: 1,
        minHeight: 0,
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      },
      stageQrTileStyles: {
        marginTop: omitCaption || slideSize ? 0 : '1vh',
        width: qrCodeProps.footprintPx,
        height: qrCodeProps.footprintPx,
        boxSizing: 'border-box' as const,
        padding: qrCodeProps.quietZonePx,
        backgroundColor: qrCodeProps.bgColor,
      },
      stageQrStyles: {
        width: '100%',
        height: '100%',
        display: 'block',
        transform: 'rotate(180deg)',
      },
    }),
    [
      captionColor,
      omitCaption,
      layoutHeight,
      layoutWidth,
      mutedAppearance,
      qrCodeProps,
      slideSize,
      wrapperBackgroundColor,
    ],
  );

  const qrTile = (
    <div
      data-name="STAGE_QR_CODE_TILE"
      className="overflow-hidden rounded-sm"
      style={stageQrTileStyles}
    >
      <QRCode
        style={stageQrStyles}
        data-name="STAGE_QR_CODE"
        value={qrValue}
        size={qrCodeProps.size}
        level={qrCodeProps.level}
        fgColor={qrCodeProps.fgColor}
        bgColor={qrCodeProps.bgColor}
      />
    </div>
  );

  return (
    <div
      style={stageQrContainerStyles}
      data-name="STAGE_QR"
      className={`flex w-full flex-col items-center rounded-lg`}
    >
      {!omitCaption &&
        (slideSize ? (
          <div data-name="STAGE_QR_TEXT_SLOT" style={stageQrCaptionSlotStyles}>
            <p
              data-name="STAGE_QR_TEXT"
              style={stageQrTextStyles}
              className={stageQrCaptionClassName(true)}
            >
              {qrCodeCta}
            </p>
          </div>
        ) : (
          <p
            data-name="STAGE_QR_TEXT"
            style={stageQrTextStyles}
            className={stageQrCaptionClassName(false)}
          >
            {qrCodeCta}
          </p>
        ))}
      {slideSize ? (
        <div data-name="STAGE_QR_REMAINING" style={stageQrRemainingStyles}>
          {qrTile}
        </div>
      ) : (
        qrTile
      )}
    </div>
  );
};
StageQR.displayName = 'StageQR';
